import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Loader2, Image as ImageIcon, Search } from 'lucide-react';

/**
 * Fase 6 — selector de media desde la Biblioteca de Conocimiento. Solo
 * assets con archivo real (imagen o video) y `public_url` — nunca expone
 * documentos internos como si fueran publicables (eso es contexto RAG,
 * no un archivo compartible).
 */
export default function MediaLibraryPicker({ open, onClose, onSelect }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/knowledge/assets', { params: { page_size: 100 } });
      const media = (data.assets || []).filter(
        (a) => a.public_url && (a.file_mime_type || '').match(/^(image|video)\//),
      );
      setAssets(media);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const filtered = assets.filter((a) => !search.trim() || (a.title || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Elegir desde la Biblioteca</DialogTitle></DialogHeader>
        <div className="relative mb-3">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[#94A3B8]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre..." className="pl-8" />
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[#94A3B8] py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Cargando...</div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#94A3B8] text-center py-8">No hay imágenes o videos en tu Biblioteca todavía.</p>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            {filtered.map((a) => (
              <button key={a.id} type="button" onClick={() => { onSelect(a); onClose?.(); }}
                className="border border-[#E2E8F0] rounded-lg overflow-hidden hover:border-[#F72585] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F72585]"
                data-testid={`library-asset-${a.id}`}>
                <div className="aspect-square bg-[#F1F5F9] flex items-center justify-center overflow-hidden">
                  {(a.file_mime_type || '').startsWith('image/') ? (
                    <img src={a.public_url} alt={a.title} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-[#94A3B8]" />
                  )}
                </div>
                <p className="text-[10px] text-[#64748B] p-1 truncate">{a.title || a.original_filename}</p>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
